import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, CalendarDays, Check, CheckCircle2, ChevronRight,
  CircleHelp, Clock, FileText, Loader2, Paperclip, Pencil, Send, ShieldCheck,
  Trash2, UploadCloud, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { RequestCallButton } from "@/components/RequestCall";
import { Card, StatusBadge } from "@/app/ui";
import { formatDate, statusLabel } from "@/lib/opportunities";

const STATUS_PRESENTATION = {
  information_provided: { label: "Information provided", className: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  missing: { label: "Information missing", className: "bg-amber-50 text-amber-700", icon: AlertTriangle },
  optional: { label: "Optional", className: "bg-slate-100 text-slate-600", icon: CircleHelp },
  not_assessed: { label: "Not assessed", className: "bg-slate-100 text-slate-600", icon: CircleHelp },
};

const EDITABLE_STATUSES = ["draft", "preparing", "ready_to_review"];

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Section = ({ id, title, kicker, action, children, testId }) => (
  <section id={id} data-testid={testId} className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7 shadow-[0_8px_30px_rgba(10,37,64,0.04)]">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        {kicker && <p className="text-xs font-bold uppercase tracking-[0.18em] text-cobalt">{kicker}</p>}
        <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-ink">{title}</h2>
      </div>
      {action}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const Info = ({ label, value }) => (
  <div>
    <dt className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">{label}</dt>
    <dd className="mt-1 break-words text-sm font-semibold text-ink">{value || "Not provided"}</dd>
  </div>
);

const ReadinessItem = ({ item }) => {
  const presentation = STATUS_PRESENTATION[item.status] || STATUS_PRESENTATION.not_assessed;
  const Icon = presentation.icon;
  return (
    <li data-testid={`readiness-${item.id}`} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${presentation.className}`}><Icon className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-ink">{item.label}</span>
          {item.required && <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Required</span>}
        </span>
        <span className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${presentation.className}`}>{presentation.label}</span>
        <span className="mt-1 block text-xs leading-5 text-slatey">{item.message}</span>
      </span>
    </li>
  );
};

const Timeline = ({ events }) => (
  <ol className="relative space-y-4 border-l border-slate-200 pl-5" data-testid="application-timeline">
    {(events || []).map((event) => (
      <li key={event.id} className="relative">
        <span className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cobalt ring-4 ring-cobalt/10" />
        <p className="text-sm font-bold text-ink">{event.title}</p>
        <p className="mt-0.5 text-xs text-slatey">{formatDateTime(event.created_at)}</p>
        {event.description && <p className="mt-1 text-sm leading-6 text-slatey">{event.description}</p>}
      </li>
    ))}
    {(!events || events.length === 0) && <p className="text-sm text-slatey">No timeline events yet.</p>}
  </ol>
);

const Modal = ({ title, onClose, children, testId }) => (
  <div data-testid={testId} className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/50 sm:items-center sm:p-6" role="dialog" aria-modal="true">
    <button className="absolute inset-0 cursor-default" aria-label="Close dialog" onClick={onClose} />
    <div className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-2xl font-bold text-ink">{title}</h2>
        <button data-testid={`${testId}-close`} onClick={onClose} className="rounded-full p-2 text-slatey hover:bg-surface" aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

const TextArea = ({ label, value, onChange, disabled, required, testId, rows = 5, placeholder }) => (
  <div>
    <label className="text-sm font-bold text-ink">
      {label} {required ? <span className="text-rose-600">*</span> : <span className="font-medium text-slatey">(optional)</span>}
    </label>
    <textarea value={value || ""} onChange={(event) => onChange(event.target.value)} disabled={disabled} rows={rows} data-testid={testId}
      placeholder={placeholder}
      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 outline-none transition-colors focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 disabled:bg-surface disabled:text-slatey" />
  </div>
);

function UploadDocumentModal({ requirement, onClose, onAttached }) {
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) {
      toast.error("Choose a file first.");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", requirement.document_type || "other");
    formData.append("display_name", displayName || requirement.label);
    try {
      const uploaded = await api.post("/individual/documents", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await onAttached(uploaded.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal title={`Upload ${requirement.label}`} onClose={onClose} testId="application-upload-modal">
      <p className="text-sm leading-6 text-slatey">This uploads to your Document Centre and then links the selected version to this application.</p>
      <div className="mt-5 space-y-4">
        <div>
          <label className="text-sm font-bold text-ink">Display name</label>
          <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} data-testid="application-upload-name"
            className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt" placeholder={requirement.label} />
        </div>
        <div>
          <label className="text-sm font-bold text-ink">File</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="application-upload-file" onChange={(event) => setFile(event.target.files[0])}
            className="mt-2 w-full text-sm text-slatey file:mr-3 file:rounded-lg file:border-0 file:bg-cobalt/10 file:px-4 file:py-2 file:font-semibold file:text-cobalt" />
        </div>
        <button onClick={upload} disabled={uploading} data-testid="application-upload-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-60">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload and attach
        </button>
      </div>
    </Modal>
  );
}

function DocumentRequirementCard({ requirement, attachments, availableDocuments, canEdit, onAttach, onRemove, onUpload }) {
  const attachment = attachments.find((item) => item.requirement_id === requirement.id);
  const candidates = availableDocuments.filter((document) => requirement.document_type === "other" || document.doc_type === requirement.document_type);
  return (
    <article data-testid={`application-document-${requirement.id}`} className="rounded-2xl border border-slate-200 bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-ink">{requirement.label}</h3>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-slatey">{requirement.required ? "Required" : "Optional"}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${attachment ? "bg-emerald-50 text-emerald-700" : requirement.required ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
          {attachment ? "Completed" : requirement.required ? "Missing" : "Optional"}
        </span>
      </div>
      {attachment ? (
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white p-4">
          <p className="flex items-center gap-2 text-sm font-bold text-ink"><Paperclip className="h-4 w-4 text-cobalt" /> {attachment.document_name}</p>
          <p className="mt-1 text-xs text-slatey">Document version {attachment.document_version} · attached {formatDateTime(attachment.attached_at)}</p>
          {canEdit && (
            <button onClick={() => onRemove(attachment)} data-testid={`document-remove-${requirement.id}`}
              className="mt-3 rounded-full border border-slate-300 px-3 py-2 text-xs font-bold text-slatey hover:border-rose-200 hover:text-rose-600">Remove document</button>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block text-sm font-bold text-ink">Select an existing document</label>
          <select value="" disabled={!canEdit} data-testid={`document-select-${requirement.id}`} onChange={(event) => event.target.value && onAttach(requirement, event.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-cobalt disabled:bg-surface">
            <option value="">{candidates.length ? "Choose a document…" : "No matching document available"}</option>
            {candidates.map((document) => <option key={document.id} value={document.id}>{`${document.display_name} · version ${document.version || 1}`}</option>)}
          </select>
          {canEdit && (
            <button onClick={() => onUpload(requirement)} data-testid={`document-upload-${requirement.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-cobalt px-4 py-2.5 text-sm font-bold text-cobalt hover:bg-cobalt/5">
              <UploadCloud className="h-4 w-4" /> Upload missing document
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export default function ApplicationDetail() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [infoResponse, setInfoResponse] = useState("");
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadRequirement, setUploadRequirement] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/individual/applications/${applicationId}`);
      setDetail(response.data);
      setForm(response.data.application.application_data || {});
      setConsent(Boolean(response.data.application.consent?.accepted));
      setDirty(false);
      setError("");
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const saveDraft = useCallback(async (showToast = false) => {
    if (!detail?.can_edit) return true;
    setSaving(true);
    try {
      const response = await api.put(`/individual/applications/${applicationId}/draft`, form);
      setDetail(response.data.application);
      setDirty(false);
      if (showToast) toast.success("Draft saved.");
      return true;
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  }, [applicationId, detail?.can_edit, form]);

  useEffect(() => {
    if (!dirty || !detail?.can_edit) return undefined;
    const timer = setTimeout(() => saveDraft(false), 900);
    return () => clearTimeout(timer);
  }, [dirty, detail?.can_edit, form, saveDraft]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  const toggleEducation = (recordId) => {
    const current = new Set(form.selected_education_ids || []);
    if (current.has(recordId)) current.delete(recordId);
    else current.add(recordId);
    updateForm("selected_education_ids", Array.from(current));
  };

  const attachDocument = async (requirement, documentId) => {
    if (dirty && !(await saveDraft(false))) return;
    try {
      const response = await api.post(`/individual/applications/${applicationId}/documents`, {
        requirement_id: requirement.id,
        document_id: documentId,
      });
      setDetail(response.data.application);
      toast.success("Document attached to application.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const removeDocument = async (attachment) => {
    try {
      const response = await api.delete(`/individual/applications/${applicationId}/documents/${attachment.id}`);
      setDetail(response.data.application);
      toast.success("Document removed from draft.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const submitApplication = async () => {
    if (!consent) {
      toast.error("Confirm the application declaration before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      if (dirty && !(await saveDraft(false))) return;
      const response = await api.post(`/individual/applications/${applicationId}/submit`, { consent: true });
      setDetail(response.data.application);
      toast.success(response.data.duplicate_prevented ? "Application was already submitted." : `Application submitted — ${response.data.application_reference}`);
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawApplication = async () => {
    try {
      const response = await api.post(`/individual/applications/${applicationId}/withdraw`, { reason: withdrawReason });
      setDetail(response.data.application);
      setWithdrawOpen(false);
      toast.success("Application withdrawn. The historical record remains available.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const deleteDraft = async () => {
    try {
      await api.delete(`/individual/applications/${applicationId}`);
      toast.success("Draft application deleted.");
      navigate("/app/applications");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const requirementById = useMemo(() => Object.fromEntries((detail?.requirements || []).map((item) => [item.id, item])), [detail]);

  if (error) {
    return (
      <Card data-testid="application-error" className="text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
        <h1 className="mt-4 font-display text-2xl font-bold text-ink">Application unavailable</h1>
        <p className="mt-2 text-sm text-slatey">{error}</p>
        <Link to="/app/applications" className="mt-5 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Back to applications</Link>
      </Card>
    );
  }

  if (!detail) return <p className="text-slatey">Loading application…</p>;

  const { application, opportunity, provider, intake, readiness, profile, attachments, available_documents: availableDocuments, timeline } = detail;
  const canEdit = detail.can_edit;
  const historicalEducation = detail.is_historical ? (detail.submitted_snapshot?.education_records || []) : detail.education_options.filter((record) => (form.selected_education_ids || []).includes(record.id));
  const missingCount = readiness?.missing_count || 0;
  const ready = Boolean(readiness?.ready);
  const personal = profile?.personal || {};
  const career = profile?.career || {};
  const international = profile?.international || {};

  return (
    <div data-testid="application-detail-page" className="pb-10">
      <Link to="/app/applications" data-testid="application-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt">
        <ArrowLeft className="h-4 w-4" /> Application centre
      </Link>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={application.status} />
            {detail.is_demo && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slatey">DEMO/SEEDED DATA</span>}
            {application.readiness_status && <span className="rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold capitalize text-cobalt">{statusLabel(application.readiness_status)}</span>}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl" data-testid="application-title">{opportunity.title}</h1>
          <p className="mt-2 text-slatey">{provider.name} · {intake.intake_label}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slatey">Reference</p>
              <p className="mt-1 font-display text-lg font-bold text-ink" data-testid="application-reference">{application.application_reference}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slatey">Starts</p>
              <p className="mt-1 font-semibold text-ink">{formatDate(intake.start_date)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slatey">Apply by</p>
              <p className="mt-1 font-semibold text-ink">{formatDate(intake.application_closing_date)}</p>
            </div>
          </div>
        </div>

        {detail.info_requests?.some((request) => request.status === "open") && (
          <Card data-testid="partner-info-request-banner" className="border-amber-300 bg-amber-50">
            {detail.info_requests.filter((request) => request.status === "open").map((request) => (
              <div key={request.id}>
                <h2 className="font-display text-xl font-bold text-ink">The team reviewing your application needs more information</h2>
                <p className="mt-2 text-sm leading-6 text-slatey">{request.message}</p>
                <textarea value={infoResponse} onChange={(event) => setInfoResponse(event.target.value)} rows={3} data-testid="info-response-input"
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm outline-none focus:border-cobalt"
                  placeholder="Reply here — add any details requested, then send." />
                <button
                  onClick={async () => {
                    setResponding(true);
                    try {
                      await api.post(`/individual/applications/${detail.application.id}/respond-info`, { message: infoResponse });
                      toast.success("Response sent to the reviewing team.");
                      setInfoResponse("");
                      await load();
                    } catch (requestError) {
                      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
                    } finally {
                      setResponding(false);
                    }
                  }}
                  disabled={responding || infoResponse.trim().length < 2}
                  data-testid="info-response-submit"
                  className="mt-3 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                  {responding ? "Sending…" : "Send response"}
                </button>
              </div>
            ))}
          </Card>
        )}

        <Card data-testid="readiness-card" className={ready ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}>
          <div className="flex items-start gap-3">
            <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${ready ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {ready ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            </span>
            <div>
              <h2 className="font-display text-xl font-bold text-ink">{ready ? "Application ready to review" : `${missingCount} item${missingCount === 1 ? "" : "s"} still required`}</h2>
              <p className="mt-1 text-sm leading-6 text-slatey">Readiness is based on information supplied. Provider confirmation is still required.</p>
            </div>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-white">
            <div className="h-2.5 rounded-full bg-cobalt transition-all" style={{ width: `${application.progress}%` }} />
          </div>
          <p className="mt-2 text-sm font-bold text-ink">{application.progress}% complete</p>
        </Card>
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2" aria-label="Application sections">
        {["profile", "education", "documents", "information", "review"].map((section) => (
          <a key={section} href={`#${section}`} data-testid={`nav-${section}`} className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold capitalize text-slatey hover:bg-surface hover:text-ink">
            {section === "information" ? "Details" : section}
          </a>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px] lg:items-start">
        <div className="space-y-6">
          <Section id="profile" title="Personal information" kicker="Reused profile" testId="application-personal-section"
            action={canEdit && <Link to="/app/profile" data-testid="edit-profile-link" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-ink"><Pencil className="h-4 w-4" /> Edit</Link>}>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Info label="Name" value={`${personal.first_name || ""} ${personal.last_name || ""}`.trim()} />
              <Info label="Preferred name" value={personal.preferred_name} />
              <Info label="Date of birth" value={formatDate(personal.date_of_birth)} />
              <Info label="Email" value={personal.email} />
              <Info label="Telephone" value={personal.telephone} />
              <Info label="Location" value={[personal.city_region, personal.country_of_residence].filter(Boolean).join(", ")} />
              <Info label="Nationality" value={personal.nationality || international.nationality} />
              <Info label="Residency" value={personal.residency_status || form.residency_information} />
            </dl>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Info label="Career" value={[career.occupation, career.industry].filter(Boolean).join(" · ")} />
              <Info label="English status" value={international.english_status} />
            </div>
          </Section>

          <Section id="education" title="Education history" kicker="Reused profile" testId="application-education-section">
            <p className="text-sm leading-6 text-slatey">Select the education records relevant to this application.</p>
            <div className="mt-4 space-y-3">
              {(canEdit ? detail.education_options : historicalEducation).map((record) => {
                const selected = canEdit ? (form.selected_education_ids || []).includes(record.id) : true;
                return (
                  <label key={record.id} data-testid={`application-education-${record.id}`}
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 ${selected ? "border-cobalt bg-cobalt/5" : "border-slate-200 bg-white"} ${!canEdit ? "cursor-default" : ""}`}>
                    <input type="checkbox" checked={selected} disabled={!canEdit} onChange={() => toggleEducation(record.id)}
                      data-testid={`education-select-${record.id}`} className="mt-1 h-4 w-4 accent-cobalt" />
                    <span>
                      <span className="block font-bold text-ink">{record.institution}</span>
                      <span className="mt-1 block text-sm text-slatey">{[record.qualification, record.subject, record.level, record.country].filter(Boolean).join(" · ")}</span>
                      {record.grade && <span className="mt-1 block text-xs text-slatey">Grade/result: {record.grade}</span>}
                    </span>
                  </label>
                );
              })}
              {detail.education_options.length === 0 && <p className="rounded-2xl bg-surface p-4 text-sm text-slatey">No education records yet. Add them in your profile, then return to this draft.</p>}
            </div>
          </Section>

          <Section id="documents" title="Documents" kicker="Document Centre integration" testId="application-documents-section">
            <p className="mb-4 rounded-2xl bg-surface p-4 text-sm leading-6 text-slatey">
              Select an existing document or upload a missing one. The application stores the document ID and version used at submission, so later replacements do not change the historical application.
            </p>
            <div className="space-y-4">
              {detail.document_requirements.map((requirement) => (
                <DocumentRequirementCard key={requirement.id} requirement={requirement} attachments={attachments} availableDocuments={availableDocuments}
                  canEdit={canEdit} onAttach={attachDocument} onRemove={removeDocument} onUpload={setUploadRequirement} />
              ))}
            </div>
          </Section>

          <Section id="information" title="Application information" kicker="Opportunity-specific" testId="application-information-section">
            <div className="space-y-5">
              <TextArea label="Personal statement" required={requirementById["personal-statement"]?.required} value={form.personal_statement}
                onChange={(value) => updateForm("personal_statement", value)} disabled={!canEdit} testId="personal-statement-input"
                placeholder="Explain your motivation, experience and suitability for this opportunity." />
              <TextArea label="Reference information" required={requirementById["reference-details"]?.required} value={form.reference_details}
                onChange={(value) => updateForm("reference_details", value)} disabled={!canEdit} testId="reference-details-input" rows={3}
                placeholder="Reference name, role, organisation and contact details, if requested." />
              <TextArea label="Residency / international information" required={requirementById["international-information"]?.required} value={form.residency_information}
                onChange={(value) => updateForm("residency_information", value)} disabled={!canEdit} testId="residency-information-input" rows={3}
                placeholder="Add application-specific residency context. Visa advice is not provided by OpenDegree." />
              <TextArea label="Additional information" value={form.additional_information}
                onChange={(value) => updateForm("additional_information", value)} disabled={!canEdit} testId="additional-information-input" rows={4}
                placeholder="Anything else the provider should know." />
              {dirty && <p className="flex items-center gap-2 text-sm font-semibold text-cobalt"><Loader2 className="h-4 w-4 animate-spin" /> Saving draft…</p>}
              {!dirty && saving && <p className="text-sm font-semibold text-cobalt">Saving draft…</p>}
            </div>
          </Section>

          <Section id="review" title="Review and submit" kicker="Final check" testId="application-review-section">
            <div className="mb-5">
              <RequestCallButton applicationId={detail.application.id} opportunitySlug={opportunity.slug} sourcePage="application_detail" testId="application-request-call" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-surface p-4">
                <h3 className="font-bold text-ink">Opportunity</h3>
                <p className="mt-2 text-sm text-slatey">{opportunity.title}</p>
                <p className="text-sm text-slatey">{provider.name}</p>
                <p className="mt-2 text-sm font-semibold text-ink">{intake.intake_label} · starts {formatDate(intake.start_date)}</p>
              </div>
              <div className="rounded-2xl bg-surface p-4">
                <h3 className="font-bold text-ink">Information supplied</h3>
                <p className="mt-2 text-sm text-slatey">{historicalEducation.length} education record(s)</p>
                <p className="text-sm text-slatey">{attachments.length} document(s) attached</p>
                <p className="text-sm text-slatey">Personal statement {(form.personal_statement || "").trim() ? "provided" : "not provided"}</p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="font-bold text-ink">Declaration and consent</h3>
              <p className="mt-2 text-sm leading-6 text-slatey">{detail.consent_text}</p>
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4">
                <input type="checkbox" checked={consent} disabled={!canEdit} onChange={(event) => setConsent(event.target.checked)}
                  data-testid="application-consent-checkbox" className="mt-1 h-4 w-4 accent-cobalt" />
                <span className="text-sm leading-6 text-ink">I confirm this declaration and consent to OpenDegree sharing the relevant application information and selected documents with {provider.name}.</span>
              </label>
              {application.consent?.accepted && <p className="mt-3 text-xs font-semibold text-slatey">Consent recorded {formatDateTime(application.consent.accepted_at)} · {application.consent.version}</p>}
            </div>

            {canEdit && (
              <button onClick={submitApplication} disabled={!ready || !consent || submitting} data-testid="submit-application-button"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-6 py-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {ready ? "Submit application" : `Complete ${missingCount} required item${missingCount === 1 ? "" : "s"}`}
              </button>
            )}
            {!canEdit && application.submitted_at && (
              <div className="mt-5 rounded-2xl bg-emerald-50 p-4 text-sm font-semibold text-emerald-700" data-testid="submitted-confirmation">
                Submitted {formatDateTime(application.submitted_at)} · Reference {application.application_reference}
              </div>
            )}
          </Section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <Section title="Application readiness" kicker="Checklist" testId="application-readiness-section">
            <ul className="space-y-3">
              {(detail.requirements || []).map((item) => <ReadinessItem key={item.id} item={item} />)}
            </ul>
          </Section>

          <Section title="Next action" kicker="Tracking" testId="application-next-action-section">
            <p className="text-sm font-semibold leading-6 text-ink">{detail.next_action}</p>
            {intake.application_closing_date && (
              <p className="mt-3 flex items-center gap-2 rounded-2xl bg-amber-50 p-3 text-sm font-semibold text-amber-700">
                <Clock className="h-4 w-4" /> Application deadline: {formatDate(intake.application_closing_date)}
              </p>
            )}
            <p className="mt-3 text-xs leading-5 text-slatey">OpenDegree supports application readiness. The Education Provider makes the final admissions decision.</p>
          </Section>

          <Section title="Timeline" kicker="Audit trail" testId="application-timeline-section">
            <Timeline events={timeline} />
          </Section>

          <Section title="Manage application" kicker="Draft controls" testId="application-manage-section">
            <div className="space-y-3">
              {canEdit && (
                <>
                  <button onClick={() => saveDraft(true)} disabled={saving} data-testid="save-draft-button"
                    className="w-full rounded-full border border-slate-300 px-4 py-3 text-sm font-bold text-ink disabled:opacity-50">
                    {saving ? "Saving…" : "Save draft"}
                  </button>
                  <button onClick={() => setDeleteOpen(true)} data-testid="delete-draft-button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-rose-200 px-4 py-3 text-sm font-bold text-rose-600">
                    <Trash2 className="h-4 w-4" /> Cancel / delete draft
                  </button>
                </>
              )}
              {detail.can_withdraw && (
                <button onClick={() => setWithdrawOpen(true)} data-testid="withdraw-application-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-rose-200 px-4 py-3 text-sm font-bold text-rose-600">
                  <X className="h-4 w-4" /> Withdraw application
                </button>
              )}
              {!canEdit && !detail.can_withdraw && <p className="text-sm text-slatey">This application is historical and read-only.</p>}
            </div>
          </Section>
        </aside>
      </div>

      {uploadRequirement && (
        <UploadDocumentModal requirement={uploadRequirement} onClose={() => setUploadRequirement(null)}
          onAttached={async (document) => {
            const requirement = uploadRequirement;
            setUploadRequirement(null);
            await attachDocument(requirement, document.id);
          }} />
      )}

      {withdrawOpen && (
        <Modal title="Withdraw application" onClose={() => setWithdrawOpen(false)} testId="withdraw-modal">
          <p className="text-sm leading-6 text-slatey">This will withdraw the application but retain the historical record and timeline. This action cannot be undone.</p>
          <TextArea label="Reason" value={withdrawReason} onChange={setWithdrawReason} testId="withdraw-reason-input" rows={4} placeholder="Optional reason for withdrawal" />
          <button onClick={withdrawApplication} data-testid="confirm-withdraw-button" className="mt-4 w-full rounded-full bg-rose-600 px-5 py-3 text-sm font-bold text-white">Confirm withdrawal</button>
        </Modal>
      )}

      {deleteOpen && (
        <Modal title="Delete draft application" onClose={() => setDeleteOpen(false)} testId="delete-draft-modal">
          <p className="text-sm leading-6 text-slatey">This permanently deletes the unsubmitted draft and its draft timeline. Submitted applications are never deleted.</p>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <button onClick={() => setDeleteOpen(false)} className="rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink">Keep draft</button>
            <button onClick={deleteDraft} data-testid="confirm-delete-draft-button" className="rounded-full bg-rose-600 px-5 py-3 text-sm font-bold text-white">Delete draft</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
